import type { ActivationLifecycle } from '@/infra/activation'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { tapAction } from '@/utils/mobx-util'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { CallCenterConfig } from '@/features/iam/workspaces/workspace-config'
import type {
  CallOption,
  CallOptionId,
} from '@/features/dashboard/components/CallAssistanceDialogs/index'
import type { LegendServiceType } from '@/features/jobs/legends/legend-service-type'
import { type ServiceSelectionViewModel } from '@/features/dashboard/DashboardView/ServiceSelectionViewModel'

export class CallCenterAssistanceViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal for selecting the call center option to get assistance.
   */
  readonly callOptionsModalVm = new ModalViewModel()

  /**
   * Modal for displaying the call center number.
   */
  readonly callSalesRepModalVm = new ModalViewModel()

  /**
   * Selected call option, if any.
   */
  @observable accessor selectedCallOption: CallOptionId | null = null

  @observable accessor selectedServiceType: LegendServiceType | null = null

  constructor(
    private getCallCenterConfig: () => CallCenterConfig,
    private readonly serviceSelectionVm: ServiceSelectionViewModel,
  ) {
    this._lifecycle.manage(
      this.callOptionsModalVm.close$.pipe(
        tapAction(() => {
          this.selectCallOption(null)
        }),
      ),
    )
  }

  /**
   * Gets the call center config.
   */
  get _callCenterConfig() {
    return this.getCallCenterConfig()
  }

  /**
   * Weather the call center is enabled.
   */
  get enabled() {
    return (
      this._callCenterConfig.enabled &&
      this._callCenterConfig.enableCallOptionsModal
    )
  }

  /**
   * Gets the call center number.
   */
  @computed
  get callCenterNumber() {
    return this._callCenterConfig.phoneNumber
  }

  /**
   * Whether to display the recommended indicator from the WS configs
   */
  @computed
  get recommendAssistedOption() {
    return this._callCenterConfig.recommendAssistedOption
  }

  /**
   * Gets the options available to display in the assistance.
   */
  @computed
  get assistanceOptions(): CallOption[] {
    return [
      {
        id: 'CallSalesRep',
        title: 'Assisted Onboarding',
        description:
          'Get immediate assistance from a representative to answer all your questions',
        icon: 'call-start-call',
        recommended: this.recommendAssistedOption,
      },
      {
        id: 'SelfOnboard',
        icon: 'legend-individual',
        title: 'Self-Onboard',
        description: 'Start on your own by answering a few quick questions',
      },
    ]
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.selectedServiceType = null
  }

  /**
   *  Shows the modal for the selected option
   */
  next() {
    if (this.selectedCallOption === 'CallSalesRep') {
      this.callSalesRepModalVm.show()
    } else if (
      this.selectedCallOption === 'SelfOnboard' &&
      this.selectedServiceType
    ) {
      this.serviceSelectionVm.selectServiceType(this.selectedServiceType)
    } else {
      this.serviceSelectionVm.startNewService()
    }

    this.callOptionsModalVm.close()
  }

  /**
   * Selects the call option.
   */
  @action.bound
  selectCallOption(option: CallOptionId | null) {
    this.selectedCallOption = option
  }

  @action.bound
  selectServiceType(type: LegendServiceType | null) {
    this.selectedServiceType = type

    this.callOptionsModalVm.show()
  }
}
